import React from 'react';
import { Button } from '../ui';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  zIndex?: string;
  /** Props extra del botón de confirmación (p.ej. data-ar-guide de las guías). */
  confirmButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & { 'data-ar-guide'?: string };
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isDestructive = true,
  zIndex = 'z-50',
  confirmButtonProps
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div className={`fixed inset-0 ${zIndex} overflow-y-auto`}>
      {/* Background overlay */}
      <div
        className="fixed inset-0 bg-ink/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal content */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative bg-paper rounded-lg shadow-overlay border border-hairline max-w-md w-full transform transition-all"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {/* Header */}
          <div className="border-b border-hairline px-6 py-4 rounded-t-lg flex justify-between items-center">
            <h3 id="modal-title" className="font-display text-lg font-semibold text-ink">{title}</h3>
            <button
              onClick={onClose}
              className="text-sage hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-mid/60 rounded-md p-1 -mr-1"
              aria-label="Cerrar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-ink mb-4">{message}</p>

            {/* Warning Icon */}
            <div className="flex items-center justify-center mb-4">
              <div className={`p-3 rounded-md ${isDestructive ? 'bg-danger/10' : 'bg-forest/10'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 ${isDestructive ? 'text-danger' : 'text-forest'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-paper-2/50 rounded-b-lg flex justify-end space-x-3">
            <Button variant="secondary" onClick={onClose}>
              {cancelText}
            </Button>

            <Button
              variant={isDestructive ? 'danger' : 'primary'}
              onClick={handleConfirm}
              {...confirmButtonProps}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
